import { useIntl } from 'react-intl';
import { FilterState, useDebouncedTableFilter, useTableFilter } from '@Component/Filter/utils.ts';
import { useHasFilter } from '@Hooks/useHasFilter';
import { useTskContext } from '@Hooks/useTskContext';
import { removeUndefinedProperties } from '@Util/misc.ts';

export function useEmployeeTableFilters(selectedPeriod: Date): FilterState {
    const intl = useIntl();
    const { showDepartmentIsWageAdmin, hasDivision, hasEmployeeCategory } = useTskContext();

    const hasContactsFilter = useHasFilter(selectedPeriod, {
        label: 'text_view_contacts',
        query: 'ONLY_CONTACTS',
        name: 'contacts',
    });
    const contactsFilter = useTableFilter({
        label: intl.formatMessage({
            id: 'text_view_contacts',
        }),
        name: 'contacts',
        query: (searchParam: string) => (searchParam === 'false' ? 'NONE' : 'INCLUDE_CONTACTS'),
        placement: 'overflow',
    });
    const employeeAvailabilityFilter = useTableFilter({
        label: intl.formatMessage({
            id: 'text_include_inactive_employees',
        }),
        name: 'employeeAvailability',
        query: (searchParam: string) => (searchParam === 'true' ? 'START_NOT_AFTER' : 'ACTIVE'),
        placement: 'overflow',
    });
    const searchFilter = useDebouncedTableFilter(
        {
            label: intl.formatMessage({ id: 'text_search' }),
            name: 'query',
        },
        250,
    );
    const employeeCategoryFilter = useTableFilter({
        label: intl.formatMessage({ id: 'text_employee_category' }),
        labelPlural: intl.formatMessage({ id: 'text_employee_categories' }),
        name: 'employeeCategoryIds',
        placement: 'overflow',
    });
    const departmentFilter = useTableFilter({
        label: intl.formatMessage({ id: 'text_department' }),
        labelPlural: intl.formatMessage({ id: 'text_departments' }),
        name: 'departmentIds',
        placement: undefined,
        className: 'tsk-filter--width200px',
    });
    const divisionFilter = useTableFilter({
        label: intl.formatMessage({ id: 'text_division' }),
        labelPlural: intl.formatMessage({ id: 'text_divisions' }),
        name: 'divisionIds',
        placement: 'overflow',
    });
    return removeUndefinedProperties({
        searchFilter,
        employeeCategoryFilter: hasEmployeeCategory ? employeeCategoryFilter : undefined,
        departmentFilter: showDepartmentIsWageAdmin ? departmentFilter : undefined,
        divisionFilter: hasDivision ? divisionFilter : undefined,
        employeeAvailabilityFilter,
        contactsFilter: hasContactsFilter ? contactsFilter : undefined,
    });
}
